import {
  getManufactureSpecsForBin,
  getSectionsFromComponentModel,
} from 'features/assetModeling'
import {
  GeckoBinnedDataBuffer,
  getMultipleCdfBinReadings,
} from 'features/dataLayers'
import sortBy from 'lodash/sortBy'
import {
  RugComponentStats,
  RugSectionMinimum,
  RugSectionStats,
} from 'pages/reporting/reports/rug/types'
import {
  GeckoAssetModel,
  GeckoBinReading,
  GeckoComponentDataFrame,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoTMinMethod,
  Section,
} from 'types'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import { PriorityQueue } from 'utils/fde/priorityQueue'

import { calculateWallLossPercent } from './calculateWallLossPercent'

export function hasSectionData(
  sectionStats: Pick<RugComponentStats, 'minThicknesses'> | undefined
): boolean {
  return (sectionStats?.minThicknesses.length ?? 0) > 0
}

function calculateThicknessStats({
  thicknessReadings,
  weldBuffers,
  thicknessUnit,
  userFacingThicknessUnit,
  lowsToCalculate,
  assetModel,
  latestDateToUse,
}: {
  thicknessReadings: readonly GeckoBinReading[]
  weldBuffers: readonly GeckoComponentRect[]
  thicknessUnit: GeckoMeasurementUnit
  userFacingThicknessUnit: GeckoMeasurementUnit
  lowsToCalculate: number
  assetModel?: GeckoAssetModel
  latestDateToUse?: Date
}): {
  minThicknesses: RugSectionMinimum[]
  averageThickness: GeckoMeasurement
  maxWallLossPercent?: GeckoMeasurement
} {
  const minimums = new PriorityQueue<RugSectionMinimum>((a, b) => {
    return b.thickness.minus(a.thickness).num
  })
  let maxWallLossPercent: GeckoMeasurement | undefined
  const zeroThickness = GeckoMeasurement.CreateFromValue(0, thicknessUnit)

  if (thicknessReadings.length === 0) {
    return {
      minThicknesses: [],
      averageThickness: zeroThickness
        .convertTo(userFacingThicknessUnit)
        .round(3),
    }
  }

  const { count, sum } = thicknessReadings.reduce(
    (agg, reading) => {
      if (
        !Number.isFinite(reading.value.num) ||
        weldBuffers.some((rect) =>
          GeckoComponentRect.Contains(rect, reading.binCoords)
        )
      ) {
        return agg
      }

      if (assetModel) {
        const { nominalThickness } = getManufactureSpecsForBin({
          assetModel,
          binBoundsToGetSpecsFor: reading.binRect,
          latestDateToUse,
        })
        if (
          nominalThickness &&
          Number.isFinite(nominalThickness.num) &&
          nominalThickness.num > 0
        ) {
          const wallLossPercent = calculateWallLossPercent(
            reading.value,
            nominalThickness
          )
          if (
            wallLossPercent &&
            (!maxWallLossPercent || wallLossPercent.gt(maxWallLossPercent))
          ) {
            maxWallLossPercent = wallLossPercent
          }
        }
      }

      const minimumsPeek = minimums.peek()
      const currCourseReading: RugSectionMinimum = {
        thickness: reading.value,
        location: reading.binCoords,
      }

      if (minimums.size() < lowsToCalculate) {
        minimums.enqueue(currCourseReading)
      } else if (
        !!minimumsPeek &&
        currCourseReading.thickness.lt(minimumsPeek.thickness)
      ) {
        minimums.dequeue()
        minimums.enqueue(currCourseReading)
      }

      return {
        count: agg.count + 1,
        sum: reading.value.plus(agg.sum),
      }
    },
    {
      count: 0,
      sum: zeroThickness,
    }
  )

  return {
    maxWallLossPercent,
    minThicknesses: sortBy(
      minimums.values().map((minimum) => {
        return {
          thickness: minimum.thickness.convertTo(userFacingThicknessUnit),
          location: minimum.location,
        }
      }),
      (minimum) => minimum.thickness.num
    ),
    averageThickness: GeckoMeasurement.CreateFromValue(
      sum.num / count,
      thicknessUnit
    )
      .convertTo(userFacingThicknessUnit)
      .round(3),
  }
}

export function getComponentSectionTMin({
  assetModel,
  componentId,
  sectionBounds,
  sectionName,
}: {
  assetModel?: GeckoAssetModel
  componentId?: string
  sectionBounds: GeckoComponentRect
  sectionName?: string
}): GeckoMeasurement | undefined {
  if (!assetModel || !componentId) {
    return undefined
  }

  const componentModel = assetModel.data.components.get(componentId)
  if (!componentModel) {
    return undefined
  }

  // Tank-floor TMin can be overridden by annular-ring features.
  if (
    componentModel.type !== GeckoComponentModelType.TankFloor &&
    'tmin' in componentModel &&
    componentModel.tmin !== undefined
  ) {
    return weightThicknessValue(componentModel.tmin)
  }

  const sections = getSectionsFromComponentModel(componentModel)

  if (!sections?.length) {
    return undefined
  }

  if (sectionName) {
    const namedSection = sections.find(
      (section) => section.name === sectionName
    )
    if (namedSection?.tmin) {
      return weightThicknessValue(namedSection.tmin)
    }
  }

  return sections.reduce<GeckoMeasurement | undefined>((maxTmin, section) => {
    if (!GeckoComponentRect.Overlaps(section.geometry, sectionBounds)) {
      return maxTmin
    }

    if (!section.tmin) {
      return maxTmin
    }

    const sectionTmin = weightThicknessValue(section.tmin)

    if (!maxTmin || sectionTmin.gt(maxTmin)) {
      return sectionTmin
    }

    return maxTmin
  }, undefined)
}

function calculateMaxTmin({
  tminCDF,
  tminBinnedDataBuffer,
  tminMethodId,
  assetModel,
  componentId,
  sectionBounds,
  sectionName,
}: {
  tminCDF?: GeckoComponentDataFrame
  tminBinnedDataBuffer?: GeckoBinnedDataBuffer
  tminMethodId?: string
  assetModel?: GeckoAssetModel
  componentId?: string
  sectionBounds: GeckoComponentRect
  sectionName?: string
}): GeckoMeasurement | undefined {
  if (tminMethodId === GeckoTMinMethod.TMinByShellCourse.id) {
    return getComponentSectionTMin({
      assetModel,
      componentId,
      sectionBounds,
      sectionName,
    })
  }

  if (tminCDF && tminBinnedDataBuffer) {
    const { range } = getMultipleCdfBinReadings({
      cdf: tminCDF,
      bounds: sectionBounds,
      binnedDataBuffer: tminBinnedDataBuffer,
    })
    if (range !== undefined) {
      return range.max
    }
  }

  return getComponentSectionTMin({
    assetModel,
    componentId,
    sectionBounds,
    sectionName,
  })
}

export function calculateRUGComponentStats({
  componentBounds,
  thicknessReadings,
  weldBuffers,
  thicknessUnit,
  userFacingThicknessUnit,
  lowsToCalculate = 3,
  tminCDF,
  tminBinnedDataBuffer,
  tminMethodId,
  assetModel,
  componentId,
  latestDateToUse,
}: {
  componentBounds: GeckoComponentRect
  thicknessReadings?: readonly GeckoBinReading[]
  weldBuffers?: readonly GeckoComponentRect[]
  thicknessUnit?: GeckoMeasurementUnit
  userFacingThicknessUnit?: GeckoMeasurementUnit
  lowsToCalculate?: number
  tminCDF?: GeckoComponentDataFrame
  tminBinnedDataBuffer?: GeckoBinnedDataBuffer
  tminMethodId?: string
  assetModel?: GeckoAssetModel
  componentId?: string
  latestDateToUse?: Date
}): RugComponentStats {
  if (!thicknessUnit || !userFacingThicknessUnit) {
    throw new Error('No thickness unit found')
  }
  if (!thicknessReadings || !weldBuffers) {
    throw new Error('No readings or weld buffers found')
  }

  const { minThicknesses, averageThickness, maxWallLossPercent } =
    calculateThicknessStats({
      thicknessReadings,
      weldBuffers,
      thicknessUnit,
      userFacingThicknessUnit,
      lowsToCalculate,
      assetModel,
      latestDateToUse,
    })

  const tMin = calculateMaxTmin({
    tminCDF,
    tminBinnedDataBuffer,
    tminMethodId,
    assetModel,
    componentId,
    sectionBounds: componentBounds,
  })

  return {
    minThicknesses,
    averageThickness,
    maxWallLossPercent,
    tMin,
  }
}

export function calculateRUGStatsForSection({
  thicknessReadings,
  weldBuffers,
  thicknessUnit,
  userFacingThicknessUnit,
  lowsToCalculate = 3,
  tminCDF,
  tminBinnedDataBuffer,
  tminMethodId,
  assetModel,
  componentId,
  section,
  sectionBounds,
  latestDateToUse,
}: {
  thicknessReadings?: readonly GeckoBinReading[]
  weldBuffers?: readonly GeckoComponentRect[]
  thicknessUnit?: GeckoMeasurementUnit
  userFacingThicknessUnit?: GeckoMeasurementUnit
  lowsToCalculate?: number
  tminCDF?: GeckoComponentDataFrame
  tminBinnedDataBuffer?: GeckoBinnedDataBuffer
  tminMethodId?: string
  assetModel?: GeckoAssetModel
  componentId?: string
  latestDateToUse?: Date
  section: Section
  sectionBounds: GeckoComponentRect
}): RugSectionStats {
  if (!thicknessUnit || !userFacingThicknessUnit) {
    throw new Error('No thickness unit found')
  }
  if (!thicknessReadings || !weldBuffers) {
    throw new Error('No readings or weld buffers found')
  }

  const { minThicknesses, averageThickness, maxWallLossPercent } =
    calculateThicknessStats({
      thicknessReadings,
      weldBuffers,
      thicknessUnit,
      userFacingThicknessUnit,
      lowsToCalculate,
      assetModel,
      latestDateToUse,
    })

  const tMin = calculateMaxTmin({
    tminCDF,
    tminBinnedDataBuffer,
    tminMethodId,
    assetModel,
    componentId,
    sectionBounds,
    sectionName: section.name,
  })

  return {
    section,
    minThicknesses,
    averageThickness,
    maxWallLossPercent,
    tMin,
  }
}
